import React, { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { apiErr } from "../../lib/utils";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { toast } from "sonner";
import {
  CalendarDays, Landmark, FileStack, ShieldCheck, Activity, Target, AlertTriangle, CircleAlert, Building2,
} from "lucide-react";

const TYPES = [
  { value: "scheme_deadline", label: "Scheme deadlines", icon: Landmark, tone: "text-[#00B4E6]" },
  { value: "application_milestone", label: "Grant claim deadlines", icon: FileStack, tone: "text-accent" },
  { value: "financing_case", label: "Financing case actions", icon: Building2, tone: "text-[#00B4E6]" },
  { value: "covenant", label: "Covenant reporting", icon: ShieldCheck, tone: "text-[#FFB800]" },
  { value: "mrv_due", label: "MRV readings", icon: Activity, tone: "text-purple-400" },
  { value: "target_milestone", label: "Target milestones", icon: Target, tone: "text-[#00E65B]" },
];
const typeOf = (v) => TYPES.find((t) => t.value === v) || TYPES[0];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

function monthKey(iso) { return iso ? iso.slice(0, 7) : "undated"; }
function monthLabel(key) {
  if (key === "undated") return "No date set";
  const [y, m] = key.split("-");
  return `${MONTHS[parseInt(m, 10) - 1]} ${y}`;
}

function DaysChip({ e }) {
  if (e.days == null) return null;
  if (e.overdue) return <Badge variant="outline" className="text-[9px] uppercase text-red-400 border-red-400/40">{Math.abs(e.days)}d overdue</Badge>;
  if (e.days === 0) return <Badge variant="outline" className="text-[9px] uppercase text-[#FFB800] border-[#FFB800]/40">Today</Badge>;
  if (e.days <= 30) return <Badge variant="outline" className="text-[9px] uppercase text-[#FFB800] border-[#FFB800]/40">in {e.days}d</Badge>;
  return <Badge variant="outline" className="text-[9px] uppercase text-muted-foreground">in {e.days}d</Badge>;
}

export default function FundingCalendar({ onNavigate }) {
  const [d, setD] = useState(null);
  const [filter, setFilter] = useState("all");
  useEffect(() => { api.get("/susfin/calendar").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e))); }, []);

  const groups = useMemo(() => {
    if (!d) return [];
    const events = (d.events || []).filter((e) => e.date && (filter === "all" || e.type === filter));
    const map = new Map();
    events.forEach((e) => {
      const k = monthKey(e.date);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(e);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [d, filter]);

  if (!d) return <div className="p-8 text-muted-foreground font-mono text-sm">Loading funding calendar…</div>;

  return (
    <div className="space-y-4" data-testid="sf-calendar">
      {(d.alerts || []).length > 0 && (
        <div className="space-y-2" data-testid="sf-cal-alerts">
          <div className="label-eyebrow text-[10px]">Alerts</div>
          {d.alerts.map((a, idx) => (
            <div key={idx} className={`flex items-center gap-3 border rounded-sm p-3 ${a.severity === "high" ? "border-red-400/40" : "border-[#FFB800]/40"}`} data-testid={`sf-cal-alert-${idx}`}>
              {a.severity === "high"
                ? <CircleAlert className="h-4 w-4 text-red-400 shrink-0" />
                : <AlertTriangle className="h-4 w-4 text-[#FFB800] shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{a.label}</div>
                <div className="text-xs text-muted-foreground truncate">{a.detail}</div>
              </div>
              {a.tab && onNavigate && (
                <Button size="sm" variant="outline" onClick={() => onNavigate(a.tab)} data-testid={`sf-cal-alert-go-${idx}`}>Go</Button>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="sf-cal-filters">
        <Button size="sm" variant={filter === "all" ? "primary" : "outline"} onClick={() => setFilter("all")} data-testid="sf-cal-filter-all">All</Button>
        {TYPES.map((t) => (
          <Button key={t.value} size="sm" variant={filter === t.value ? "primary" : "outline"} onClick={() => setFilter(t.value)} data-testid={`sf-cal-filter-${t.value}`}>
            <t.icon className="h-3 w-3" /> {t.label}
          </Button>
        ))}
      </div>

      {groups.length === 0 && (
        <div className="card-flat p-12 text-center text-muted-foreground" data-testid="sf-cal-empty">
          <CalendarDays className="h-8 w-8 mx-auto mb-3 opacity-50" />
          No dated events {filter !== "all" ? "of this type" : ""} on the funding calendar yet.
        </div>
      )}

      {groups.map(([key, events]) => (
        <div key={key} data-testid={`sf-cal-month-${key}`}>
          <div className="label-eyebrow mb-2">{monthLabel(key)}</div>
          <div className="space-y-1.5">
            {events.map((e, idx) => {
              const T = typeOf(e.type);
              return (
                <div key={`${key}-${idx}`} className={`flex items-center gap-3 card-flat p-3 ${e.overdue ? "border-red-400/30" : ""}`} data-testid={`sf-cal-event-${e.type}-${idx}`}>
                  <div className="font-mono text-xs w-20 shrink-0 text-muted-foreground">{e.date}</div>
                  <T.icon className={`h-4 w-4 shrink-0 ${T.tone}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{e.label}</div>
                    <div className="text-xs text-muted-foreground truncate">{e.detail}</div>
                  </div>
                  <DaysChip e={e} />
                  {e.tab && onNavigate && (
                    <button onClick={() => onNavigate(e.tab)} className="text-[10px] text-accent hover:underline shrink-0" data-testid={`sf-cal-event-go-${e.type}-${idx}`}>Open</button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground">{d.disclaimer}</p>
    </div>
  );
}
